import { datadogLogs } from '@datadog/browser-logs'
import { PfmContext } from 'containers/PageFlowManager/PageFlowManager'
import { AppState, AppStateContext } from 'containers/State/AppState'
import { PanelSupplierCode } from 'model/common'
import { AddMaxDiffResponsePayload } from 'model/newApiPayloads'
import { ResponseOptionChoice } from 'model/responseChoice'
import { useContext } from 'react'
import { coreRespondentUrl } from 'utils/coreRespondentUrl'
import { API_DISTRIBUTION_FACET } from 'utils/datadog'
import { getUtcOffset } from 'utils/datetime'
import { usePostCallbackWithRetry } from './usePostCallback'
import { useQuestionnaireParams } from './useQuestionnaireParams'

const useSendMaxDiffResponse = () => {
  const post = usePostCallbackWithRetry<AddMaxDiffResponsePayload>()
  const { surveyId, respondentId, panelSupplier } = useQuestionnaireParams()
  const {
    respondentProgress: [progress, setProgress]
  } = useContext<AppState>(AppStateContext)
  const { next } = useContext(PfmContext)

  const sendMaxDiffResponse = async ({
    questionId,
    entryPosition,
    renderedEntryPosition,
    contextPosition,
    renderedContextPosition,
    questionTypeCode,
    responseChoices,
    experimentalDesignVersion
  }: {
    questionId: string
    entryPosition: number
    renderedEntryPosition: number
    contextPosition: number
    renderedContextPosition: number
    questionTypeCode: string
    responseChoices: ResponseOptionChoice[]
    experimentalDesignVersion: number
  }) => {
    const payload: AddMaxDiffResponsePayload = {
      supplier_respondent_id: respondentId,
      panel_supplier_code: panelSupplier as PanelSupplierCode,
      question_id: questionId,
      rendered_entry_position: renderedEntryPosition,
      experimental_design_version: experimentalDesignVersion,
      max_diff_choices: responseChoices.map((choice) => ({
        response_option_id: choice.responseOptionId!,
        max_diff_choice_value: choice.value,
        response_choice_set_number: choice.setNumber!
      })),
      entry_position: entryPosition,
      previous_entry_position: progress?.previousEntryPosition ?? null,
      context_position: contextPosition,
      rendered_context_position: renderedContextPosition,
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      utc_offset: getUtcOffset()
    }
    const url = coreRespondentUrl(`/surveys/${surveyId}/responses/max_diff`)

    const requestStartTime = performance.now()

    const promise = post(payload, url)
      .then(() => {
        const requestDuration = performance.now() - requestStartTime
        datadogLogs.logger.info('MaxDiff response was sent', {
          questionId,
          questionTypeCode,
          surveyId,
          respondentId,
          apiUsed: API_DISTRIBUTION_FACET.NEW,
          requestDurationMs: Math.round(requestDuration),
          payloadSize: JSON.stringify(payload).length
        })
      })
      .catch((err) => {
        const requestDuration = performance.now() - requestStartTime
        datadogLogs.logger.error(
          'Network Failure: MaxDiff response failed to be sent',
          {
            err,
            httpStatusCode: err?.response?.status ?? null,
            questionId,
            questionTypeCode,
            surveyId,
            respondentId,
            apiUsed: API_DISTRIBUTION_FACET.NEW,
            payloadSize: JSON.stringify(payload).length,
            requestDurationMs: Math.round(requestDuration)
          }
        )
        if (progress) {
          setProgress({
            ...progress,
            isQualityTerminated: true
          })
          next()
        } else {
          throw new Error(
            'Could not find respondent progress in useSendMaxDiffResponse'
          )
        }
      })

    return promise
  }

  return { sendMaxDiffResponse }
}

export default useSendMaxDiffResponse
